// @flow

import React from 'react';
import { get, isEmpty } from 'lodash';

import {
  commonErrors as Errors,
  selectErrorMessages,
} from '@zapier/toolbox-redux-framework';

import InlineUpgradeTriggerModal from 'app/common/components/InlineUpgradeTrigger/InlineUpgradeTriggerContainer';
import ErrorInfo from 'app/common/components/ErrorInfo';
import LazyLoadDestinationAuthModal from 'app/common/components/LazyLoadDestinationAuthModal';
import LearnMoreErrorInfo from 'app/common/components/LearnMoreErrorInfo';
import Modal from 'app/common/components/Modal';
import UnpauseErrorModal from 'app/common/components/UnpauseErrorModal';
import { isDataFlowProtectionRequired } from 'app/common/utils/ErrorUtils';

type ErrorModalProps = {
  onClose: () => void,
  errors: Array<Object>,
  isFromContactForm?: boolean,
};

const ErrorModal = ({
  onClose,
  errors,
  isFromContactForm,
}: ErrorModalProps) => {
  let error = errors[errors.length - 1];
  // First check if any error is due to account limits (so we can show a CTA to
  // upgrade).
  // Consider all the checks that were run on the zap and see if any are upgrade
  // related.
  const upgradeError = errors.find(
    err => !isEmpty(get(err, ['json', 'checks']))
  );

  if (upgradeError) {
    const upgradeErrorCheck = upgradeError.json.checks.find(err =>
      get(err, ['upgrade_required'])
    );

    if (upgradeErrorCheck) {
      const plans = get(upgradeErrorCheck, ['cta', 'plans']);
      const upgradeToPlan = get(plans, 'annual') || get(plans, 'monthly');
      const upgradeReason = get(upgradeErrorCheck, ['cta', 'reasons', '0']);

      // Extract app name from reason string
      const matches = get(upgradeReason, 'reason', '').match(/\((\w+|\d+)\)/);
      const appName = get(matches, '0', '').replace(/\(|\)/gi, '');

      if (upgradeToPlan) {
        return (
          <InlineUpgradeTriggerModal
            appName={appName || null}
            autoOpen={true}
            flowId={get(upgradeError, 'flowId')}
            onClose={onClose}
            upgradeToPlan={upgradeToPlan}
            validationReason={get(upgradeReason, 'extension')}
          />
        );
      }
    }
  }

  if (isDataFlowProtectionRequired(errors)) {
    return (
      <LazyLoadDestinationAuthModal
        checks={(error.json || {}).checks}
        flowId={error.flowId}
        onCloseModal={onClose}
      />
    );
  }

  if (
    error &&
    error.status === 403 &&
    error.name === 'UnpauseError' &&
    error.flowId
  ) {
    return (
      <UnpauseErrorModal
        checks={(error.json || {}).checks}
        flowId={error.flowId}
        onCloseModal={onClose}
      />
    );
  }

  let ErrorType = error && error.name && Errors[error.name];

  if (!ErrorType) {
    error = new Errors.UnknownError(error);
    ErrorType = Errors.UnknownError;
  }

  const summary = ErrorType.summary(error);
  const errorsFound = ErrorType.errors(error);
  const messages = selectErrorMessages(errorsFound);

  const content = ErrorType.hasHelpLink(error) ? (
    <LearnMoreErrorInfo
      continueText="or continue to Dashboard"
      helpLink={ErrorType.helpLink(error)}
      onClose={onClose}
      summary={ErrorType.summary(error)}
      title="There's an Error With This Zap"
    />
  ) : (
    <ErrorInfo
      isFromContactForm={isFromContactForm}
      messages={messages}
      status={error.status}
      summary={summary}
    />
  );

  return (
    <Modal
      closeOnEsc={true}
      closeOnOutsideClick={true}
      isOpened={true}
      onClose={onClose}
    >
      {content}
    </Modal>
  );
};

export default ErrorModal;
